/* eslint import/no-unresolved: 0 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { sendTrackEvent, sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Button } from '@openedx/paragon';

import { UpgradeButton } from '@src/generic/upgrade-button';
import { UpsellTable } from '../generic';
import BaseModal from './BaseModal';
import { useCohesion } from '../cohesion';

const UpgradeModal = ({
  isOpen,
  close,
  verifiedMode,
  org,
  courseId,
  offer,
  marketingUrl,
  accessExpiration,
  datesBannerInfo,
  learningAssistantEnabled,
}) => {
  const upsellPageName = 'course_sequence_modal';
  const eventProperties = {
    org_key: org,
    courserun_key: courseId,
  };
  const promotionEventProperties = {
    creative: 'marketmodalupsell',
    name: 'In-Course Verification Prompt',
    position: 'marketing-modal-message',
    promotion_id: 'courseware_verified_certificate_upsell',
    ...eventProperties,
  };

  const { conversion } = useCohesion(courseId);

  useEffect(() => {
    sendTrackingLogEvent('edx.bi.course.upgrade.marketmodalupsell.displayed', eventProperties);
    sendTrackEvent('Promotion Viewed', promotionEventProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logClick = () => {
    sendTrackingLogEvent('edx.bi.course.upgrade.marketingmodal.clicked', eventProperties);
    sendTrackingLogEvent('edx.course.enrollment.upgrade.clicked', {
      ...eventProperties,
      location: 'marketing-modal-message',
    });
    sendTrackEvent('Promotion Clicked', promotionEventProperties);
    sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
      ...eventProperties,
      linkCategory: 'green_upgrade',
      linkName: `${upsellPageName}_green`,
      linkType: 'button',
      pageName: upsellPageName,
    });

    conversion('Upgraded');
  };
  const logClickPastExpiration = () => {
    sendTrackEvent('edx.bi.ecommerce.upgrade_modal.past_expiration.button_clicked', {
      ...eventProperties,
      linkCategory: 'upgrade_modal',
      linkName: `${upsellPageName}_course_details`,
      linkType: 'button',
      pageName: upsellPageName,
    });
  };

  const intl = useIntl();
  const dateNow = Date.now();
  const upgradeDeadlineDate = accessExpiration ? new Date(accessExpiration.upgradeDeadline) : null;
  const pastDeadline = accessExpiration ? new Date(dateNow) > upgradeDeadlineDate : false;
  const isFBE = !!accessExpiration && !!datesBannerInfo?.contentTypeGatingEnabled;

  let callToActionButton = (
    <UpgradeButton
      data-testid="upgrade-button"
      offer={offer}
      onClick={logClick}
      verifiedMode={verifiedMode}
      block
    />
  );

  if (pastDeadline) {
    callToActionButton = (
      <Button
        className="w-100"
        onClick={logClickPastExpiration}
        href={marketingUrl}
      >
        {intl.formatMessage(
          {
            id: 'learning.upgrade-modal.upgrade-button.label',
            defaultMessage: 'View course details',
            description: 'Label for upgrade button',
          },
        )}
      </Button>
    );
  }

  return (
    <BaseModal
      {...{
        isOpen,
        close,
        courseId,
        offer,
        callToActionButton,
      }}
    >
      <UpsellTable {...{ isFBE, learningAssistantEnabled }} />;
    </BaseModal>
  );
};

UpgradeModal.defaultProps = {
  verifiedMode: {},
  offer: null,
  accessExpiration: null,
  datesBannerInfo: null,
};
UpgradeModal.propTypes = {
  verifiedMode: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currencySymbol: PropTypes.string.isRequired,
    upgradeUrl: PropTypes.string.isRequired,
  }),
  offer: PropTypes.shape({
    code: PropTypes.string,
  }),
  accessExpiration: PropTypes.shape({
    upgradeDeadline: PropTypes.string,
  }),
  datesBannerInfo: PropTypes.shape({
    contentTypeGatingEnabled: PropTypes.bool,
  }),
  marketingUrl: PropTypes.string.isRequired,
  org: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  learningAssistantEnabled: PropTypes.bool.isRequired,
};

export default UpgradeModal;
